import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Users, Search, Sparkles, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const BAND_LABEL = {
  VERY_CLOSE_MATCH: "Very close match",
  STRONG_MATCH: "Strong match",
  GOOD_MATCH: "Good match",
  LOW_MATCH: "Emerging match",
};

const STAGE_LABEL = {
  coursework: "Coursework", comps: "Comprehensive Exams",
  topic_development: "Topic Development", proposal: "Proposal Development",
  proposal_defense: "Proposal Defense", irb: "IRB / Ethics Approval",
  research: "Data Collection", analysis: "Data Analysis",
  dissertation: "Dissertation Writing", final_review: "Final Review",
  defense: "Dissertation Defense", revisions: "Revisions",
  graduation: "Graduation", publishing: "Publishing / Career Development",
};

function PeerCard({ p }) {
  const stageLabel = STAGE_LABEL[p.current_stage] || p.current_stage || "";
  return (
    <Card className="p-6" data-testid={`peer-card-${p.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-primary" data-testid={`peer-band-${p.id}`}>
            {BAND_LABEL[p.match?.band] || "Match"}
          </p>
          <h3 className="font-serif text-2xl font-black tracking-tight mt-1">
            <Link to={`/people/${p.id}`} className="hover:underline underline-offset-4" data-testid={`peer-open-${p.id}`}>{p.display_name || "Doctoral Candidate"}</Link>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {[p.degree_type, p.discipline].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {[p.methodology, p.methodology_subtype].filter(Boolean).join(" · ")}
            {stageLabel && <> · {stageLabel}</>}
          </p>
          {p.match?.reasons?.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Why you match</p>
              {p.match.reasons.slice(0, 4).map((r, i) => (
                <p key={i} className="text-[12px] text-foreground/80 leading-snug" data-testid={`peer-reason-${p.id}`}>· {r}</p>
              ))}
            </div>
          )}
          {p.shared_circles_count > 0 && (
            <p className="mt-3 text-[11px] text-primary/80" data-testid={`peer-shared-${p.id}`}>
              You share {p.shared_circles_count} {p.shared_circles_count === 1 ? "Circle" : "Circles"}
              {p.shared_circles_names?.length > 0 && <> · {p.shared_circles_names.join(", ")}</>}
            </p>
          )}
        </div>
        <Link to={`/people/${p.id}`}>
          <Button size="sm" variant="outline" className="rounded-full" data-testid={`peer-view-${p.id}`}>View profile</Button>
        </Link>
      </div>
    </Card>
  );
}

export default function People() {
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 12;

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = { limit, offset: opts.offset ?? offset };
      if (q.trim()) params.q = q.trim();
      if (stage) params.doctoral_stage = stage;
      const r = await api.get("/people", { params });
      setItems(opts.append ? [...items, ...r.data.results] : r.data.results);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, stage, offset, items]);

  useEffect(() => { load({ offset: 0 }); setOffset(0); /* eslint-disable-next-line */ }, []);

  const runSearch = () => { setOffset(0); load({ offset: 0 }); };
  const loadMore = () => {
    const next = offset + limit;
    setOffset(next);
    load({ offset: next, append: true });
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="people-page">
      <p className="text-xs uppercase tracking-[0.25em] text-primary">Scholarly peer discovery</p>
      <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-3">People walking your path.</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">Doctoral candidates matched to you by degree, discipline, methodology, and stage — never by algorithm or engagement.</p>

      <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 text-primary" /> Explainable matches only — no AI ranking.
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
                 placeholder="Search by name or research topic" className="pl-9 w-72" data-testid="people-search" />
        </div>
        <select value={stage} onChange={(e) => { setStage(e.target.value); setOffset(0); setTimeout(runSearch, 50); }}
                data-testid="people-stage-filter"
                className="h-9 px-3 rounded-md border border-border bg-background text-sm">
          <option value="">All doctoral stages</option>
          {Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Button size="sm" onClick={runSearch} className="rounded-full" data-testid="people-search-btn">Search</Button>
      </div>

      {items.length === 0 && !loading ? (
        <Card className="mt-10 p-10 text-center" data-testid="people-empty">
          <GraduationCap className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="mt-4 font-serif text-2xl font-black tracking-tight">No scholarly matches yet.</p>
          <p className="text-sm text-muted-foreground mt-2">Finish your profile — discipline, methodology, and stage — and we'll find peers walking the same path.</p>
        </Card>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="people-grid">
          {items.map((p) => <PeerCard key={p.id} p={p} />)}
        </div>
      )}

      {items.length < total && (
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={loadMore} disabled={loading} className="rounded-full" data-testid="people-load-more">
            {loading ? "Loading…" : "See more people"}
          </Button>
        </div>
      )}
    </div>
  );
}
